import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { ItemListEditor } from '@/components/administration/item-list-editor'
import type { GroupItem } from '@/components/grouped-list'
import { Button } from '@/components/ui/button'
import { Field, FieldContent, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { Spinner } from '../ui/spinner'
import { SpheraFlagSelect } from './sphera-flag-select'

export type Classification = GroupItem

export interface ClassificationFormProps {
	mode: 'create' | 'edit'
	initial?: Classification
	onSave: (data: {
		name: string
		spheraName: string | null
		subclassifications: string[]
	}) => void
	onClose: () => void
}

export function ClassificationFormContent({
	mode,
	initial,
	onSave,
	onClose,
}: ClassificationFormProps) {
	const { t } = useTranslation()
	const [disableSaving, setDisableSaving] = useState(false)
	const [name, setName] = useState(initial?.name ?? '')
	const [spheraName, setSpheraName] = useState(initial?.spheraName ?? '')
	const [subs, setSubs] = useState<string[]>(
		initial?.children.map((s) => s.name) ?? [''],
	)

	const handleSave = async () => {
		if (disableSaving) return

		if (!name.trim()) {
			toast.error(
				t('administration.classificationsForm.emptyClassificationError'),
			)
			return
		}
		const filled = subs.filter((s) => s.trim())
		if (filled.length === 0) {
			toast.error(
				t('administration.classificationsForm.emptySubclassificationError'),
			)
			return
		}

		setDisableSaving(true)
		try {
			await Promise.resolve(
				onSave({
					name: name.trim(),
					spheraName: spheraName || null,
					subclassifications: filled,
				}),
			)
		} catch (error) {
			toast.error(`Erro ao salvar ${String(error)}`)
		}
	}

	return (
		<div className="flex min-h-0 flex-1 flex-col gap-5 px-1">
			{/* Classification name */}
			<Field>
				<FieldLabel htmlFor="classification-name">
					{t('administration.classificationsForm.classificationLabel')}
				</FieldLabel>
				<FieldContent>
					<Input
						id="classification-name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder={t(
							'administration.classificationsForm.classificationPlaceholder',
						)}
					/>
				</FieldContent>
			</Field>

			<SpheraFlagSelect value={spheraName} onChange={setSpheraName} />

			<Separator className="bg-border/60" />

			<div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
				{/* Subclassifications list */}
				<ItemListEditor
					label={t('administration.classifications.subclassifications')}
					addLabel={t('administration.classifications.addSubclassification')}
					placeholder={t(
						'administration.classificationsForm.subclassificationLabel',
					)}
					items={subs}
					onChange={setSubs}
					minItems={1}
				/>
			</div>

			{/* Footer buttons */}
			<div className="flex shrink-0 gap-3 pt-1">
				<Button
					type="button"
					variant="cancel"
					onClick={onClose}
					className="flex-1"
				>
					{t('administration.classificationsForm.cancelButton')}
				</Button>
				<Button
					type="button"
					onClick={handleSave}
					disabled={disableSaving}
					className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
				>
					{mode === 'create' ? (
						disableSaving ? (
							<>
								<Spinner /> {t('administration.classificationsForm.saving')}
							</>
						) : (
							t('administration.classificationsForm.saveButton')
						)
					) : disableSaving ? (
						<>
							<Spinner /> {t('administration.classificationsForm.saving')}
						</>
					) : (
						t('administration.classifications.saveEdit')
					)}
				</Button>
			</div>
		</div>
	)
}
